import React, {FunctionComponent, useState} from "react";
import {
  BookingDocumentData,
  BookingDocumentFormCategory,
  BookingDocumentFormData,
  BookingStatus
} from "../../../../interfaces/BookingInterfaces";
import {Card, CardBody, CardHeader, Input, Label} from "reactstrap";
import {FormattedMessage, useIntl} from "react-intl";
import Icon from "../../../icon/Icon";
import {ColorType, IconSizeType} from "../../../../types/bootstrap/BootstrapType";
import Button from "../../../atoms/Button";
import FieldLabel from "../../../atoms/FieldLabel";
import FormInput from "../../input/FormInput";
import {FormikErrors, FormikTouched} from "formik";
import {FileType} from "constants/FileConstants";
import ErrorField from "../../input/ErrorField";
import {DocumentDTO} from "../../../../interfaces/FileInterfaces";
import {bookingService} from "../../../../services/BookingService";
import {fileUtils} from "../../../../utils/fileUtils";
import {toastUtils} from "../../../../utils/toastUtils";
import DocumentItem from "./DocumentItem";
import FileUploadZones from "./FileUploadZones";
import ModalBillBooking from "../../modal/ModalBillBooking";

interface BookingDocumentFormCardProps {
  className?: string;
  category: BookingDocumentFormCategory
  bookingDocumentData: BookingDocumentData;
  errors: FormikErrors<BookingDocumentFormData>
  touched: FormikTouched<BookingDocumentFormData>
  values: BookingDocumentFormData
  onChangeValue: (field: string, value: unknown) => void
  onDeleteDocument: (documentId: string) => void
  onRefreshBookingDetails?: () => void
}

const BookingDocumentFormCard: FunctionComponent<BookingDocumentFormCardProps> = ({
  category,
  bookingDocumentData,
  className = "",
  errors,
  touched,
  values,
  onChangeValue,
  onDeleteDocument,
  onRefreshBookingDetails,
}) => {

  const intl = useIntl();
  const [addFile, setAddFile] = useState<boolean>(false);
  const [billModalOpen, setBillModalOpen] = useState<boolean>(false);

  const documents: DocumentDTO[] = bookingDocumentData[category?.idDocument];

  const downloadDocument = (fileId: string, name: string, extension: string) => {
    const filePromise: Promise<Blob> = bookingService.getDocumentById(fileId);
    fileUtils.downloadFile(filePromise, { name, extension }, intl);
  }

  const handleFileDetailsChange = (fileId: string, field: "invoiceNumber" | "invoiceDate", value: string) => {
    const currentDetails = values.fileDetails?.[fileId] || { invoiceNumber: "", invoiceDate: "" };
    const updatedFileDetails = {
      ...values.fileDetails,
      [fileId]: {
        ...currentDetails,
        [field]: value
      }
    };
    onChangeValue("fileDetails", updatedFileDetails);
  };

  const handleDocumentDelete = (document: DocumentDTO) => {
    if (!documents.some(doc => doc.fileId === document.fileId && doc.fileType === FileType.BILL)) {
      onChangeValue("atLeastOneBillFile", false);
    }
    onDeleteDocument(document.fileId);
  };

  const onChangeBookingFile = async (file: FileList, fileType: FileType) => {
    try {
      if (!bookingDocumentData.id) return
      const uploadedDocuments = await bookingService.addFiles(
        bookingDocumentData.id,
        file,
        { fileType }
      );

      const newDocuments = Array.isArray(uploadedDocuments) ? uploadedDocuments : [uploadedDocuments];

      // For non-BILL files, store them in directDocuments by category
      const categoryKey = category.idDocument; // e.g., "supportingDocuments", "paymentDocuments"
      const currentDirectDocs = values.directDocuments || {};
      const currentCategoryDocs = currentDirectDocs[categoryKey] || [];
      const updatedDirectDocs = {
        ...currentDirectDocs,
        [categoryKey]: [...currentCategoryDocs, ...newDocuments]
      };
      onChangeValue("directDocuments", updatedDirectDocs);

      // Show success message for non-BILL files
      toastUtils.successToast(intl.formatMessage({id: "success_toast_add_booking_file"}));

      // Update UI indicator for PAYMENT files
      if (fileType === FileType.PAYMENT && !values.atLeastOnePaymentFile) {
        onChangeValue("atLeastOnePaymentFile", true);
      }
    } catch (error) {
      // Handle upload error
      toastUtils.errorToast(intl.formatMessage({id: "booking_error_uploading_file"}));
      console.error("Error uploading file:", error);
    }
    setAddFile(false);
  };

  // Get directly uploaded documents for this category
  const directDocuments = values.directDocuments?.[category.idDocument] || [];

  const onRemoveDirectDocument = (directDoc: DocumentDTO) => {
    // Remove from directDocuments
    const currentDirectDocs = values.directDocuments || {};
    const categoryKey = category.idDocument;
    const currentCategoryDocs = currentDirectDocs[categoryKey] || [];
    const updatedCategoryDocs = currentCategoryDocs.filter(doc => doc.fileId !== directDoc.fileId);

    const updatedDirectDocs = {
      ...currentDirectDocs,
      [categoryKey]: updatedCategoryDocs
    };
    onChangeValue("directDocuments", updatedDirectDocs);

    // Update file type indicators if needed
    if (directDoc.fileType === FileType.PAYMENT && updatedCategoryDocs.length === 0) {
      onChangeValue("atLeastOnePaymentFile", false);
    }

    // Call the delete API
    bookingService.deleteDocumentById(directDoc.fileId)
      .then(() => toastUtils.successToast(intl.formatMessage({id: "file_deleted_success"})))
      .catch(() => toastUtils.errorToast(intl.formatMessage({id: "error_deleting_file"})));
  };

  return (
    <>
      <Card className={`mt-2 form-document-card ${className}`}>
        <CardHeader className={`fw-bold ${values[category.idCheckbox] && "success"}`}>
          <div className="d-flex">
            <Input
              id={category.idCheckbox}
              className="epow-checkbox-input rounded-circle me-2 cursor-pointer"
              type="checkbox"
              checked={values[category.idCheckbox]}
              disabled={
                category.idCheckbox === "isBilled"
                  ? false
                  : ((bookingDocumentData?.status) === BookingStatus.DOCUMENT_RECEIVED
                    && ((bookingDocumentData?.isPaid && values[category.idCheckbox]) || category.disableWhenUnpaid))
              }
              onClick={() => onChangeValue(category.idCheckbox, !values[category.idCheckbox])}/>
            <FormattedMessage id={category.title}/>
          </div>
        </CardHeader>
      <CardBody>
        {((bookingDocumentData?.documentCount) > 0 || directDocuments.length > 0) &&
          <div className="mb-3">
            {documents.map(document => (
              <DocumentItem
                key={document.fileId}
                document={document}
                category={category}
                values={values}
                bookingStatus={bookingDocumentData.status}
                onDownload={downloadDocument}
                onChangeFileDetails={handleFileDetailsChange}
                onDelete={handleDocumentDelete}
              />
            ))}

            {directDocuments.map((directDoc) => (
              <DocumentItem
                key={`direct-${directDoc.fileId}`}
                document={directDoc}
                category={category}
                values={values}
                bookingStatus={bookingDocumentData.status}
                onDownload={downloadDocument}
                onChangeFileDetails={handleFileDetailsChange}
                onDelete={onRemoveDirectDocument}
              />
            ))}

            {errors.fileDetails && category.fileType === FileType.BILL && (
              <div className="epow-error d-flex justify-content-center">
                <Label>{errors.fileDetails.toString()}</Label>
              </div>)
            }
          </div>
        }

        {category.hasDate && values[category.editDateLabel] &&
          <FormInput
          id={category.idDate}
          label={category.dateLabel}
          type="date"
          className="w-50"
          required={values[category.editDateLabel]}
          value={values[category.idDate]}
          onChange={(e) => onChangeValue(category.idDate, e.target?.value)}
          error={errors[category.idDate]}
          touched={touched[category.idDate]}
          />
        }
        {category.hasDate && !values[category.editDateLabel] &&
          <div className="d-flex justify-content-between">
            <div>
              <FieldLabel label={intl.formatMessage({id: category.dateLabel})}
                          value={bookingDocumentData[category?.idDate]}
              />
              <ErrorField error={errors[category.idDate]} touched={touched}/>
            </div>
            {
              bookingDocumentData?.status !== BookingStatus.CLOSED &&
              <Icon name="Pen" size={IconSizeType.XS} className="cursor-pointer m-0"
                    onClick={() => onChangeValue(category.editDateLabel, true)}/>
            }
          </div>
        }

        <div className="mt-3">
          {addFile ? (
            <div className="d-flex justify-content-between align-items-center">
              <FileUploadZones
                category={category}
                onFileChange={onChangeBookingFile}
              />
            </div>
          ) : (
            <div className="d-flex justify-content-around">
              { bookingDocumentData?.status !== BookingStatus.CLOSED &&
                <Button
                  color={ColorType.SECONDARY}
                  className="border border-dark mx-1"
                  onClick={() => {
                    // For BILL type, open modal instead of file upload
                    if (category.fileType === FileType.BILL) {
                      setBillModalOpen(true);
                    } else {
                      setAddFile(true);
                    }
                  }}>
                  <FormattedMessage id={category.addButtonLabel}/>
                  <Icon className="ms-2" name={category.addButtonIcon}/>
                </Button>
              }
              {
                (category.extraButtonLabel && bookingDocumentData?.status !== BookingStatus.CLOSED && category.shouldDisplayExtraButton(bookingDocumentData?.isPaid)) && (
                  <Button
                    color={ColorType.SECONDARY}
                    className="border border-dark mx-1"
                    onClick={() => category.extraButtonAction()}>
                    <FormattedMessage id={category.extraButtonLabel}/>
                    <Icon className="ms-2" name={category.extraButtonIcon}/>
                  </Button>
                )
              }
            </div>
          )}
        </div>
      </CardBody>
    </Card>

    {category.fileType === FileType.BILL && (
      <ModalBillBooking
        bookingId={bookingDocumentData?.id}
        modalOpen={billModalOpen}
        onOpenModal={setBillModalOpen}
        onDocumentAdded={onRefreshBookingDetails}
      />
    )}
    </>
  )
}

export default BookingDocumentFormCard;
